import cx from "classnames";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Column, Container, getIsUserSignedIn, getUser, Row } from "@gamedev-web/gamedev-sdk-core";

import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { Modal } from "../../components/Modal/Modal";
import { signInUrl, siteLocationPrefix } from "../../constants";
import { useMinPageHeight } from "../../helpers/windowHelpers";
import { PartnerCenterSeller } from "../../models/ticketing";
import {
  getMiddlewarePartners,
  getPartnerCenterSellers,
} from "../../redux/selectors/partnerCenterSelectors";
import { sellerHasGdkSwRole } from "../../server/partnerCenter/helpers/gdkMiddlewareRequestHelpers";
import { MiddlewarePartner } from "../../server/partnerCenter/models/middlewarePartner";
import {
  cancel,
  confirmAccessRequest,
  gdkRequestTitle,
  submitRequest,
  submitRequestButton,
  thirdPartyPrivacyNotice,
} from "./gdkMiddlewarePageContent";
import * as styles from "./GdkMiddlewareRequestPage.module.css";
import { isGdkEmailEnabledForOverride, isGdkMiddlewareReadyToSubmit } from "./helpers";
import { GdkMiddlewareRequest, GdkMiddlewareStep } from "./models";
import {
  MainRequestStep,
  NoPartnerCenterAccountStep,
  SubmissionErrorStep,
  SubmittedStep,
  SubmittingStep,
  UnauthorizedStep,
} from "./steps";

const sendGdkMiddlewareUrl = `${siteLocationPrefix}/gdkMiddleware/send`;

const GdkMiddlewareRequestPage = () => {
  const isSignedIn = useSelector(getIsUserSignedIn);
  const minPageHeight = useMinPageHeight();
  const sellers = useSelector(getPartnerCenterSellers);
  const middlewarePartners = useSelector(getMiddlewarePartners);
  const [currentSeller, setCurrentSeller] = useState<PartnerCenterSeller | undefined>(undefined);
  const [currentMiddleware, setCurrentMiddleware] = useState<MiddlewarePartner | undefined>(
    undefined
  );
  const [currentPageStep, setCurrentPageStep] = useState(GdkMiddlewareStep.NoPartnerCenterAccount);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const user = useSelector(getUser);
  const [email, setEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isSignedIn) {
      window.location.href = signInUrl;
      return;
    }

    if (sellers.length > 0) {
      if (sellers.some(seller => sellerHasGdkSwRole(seller))) {
        setCurrentPageStep(GdkMiddlewareStep.MainRequest);
      } else {
        setCurrentPageStep(GdkMiddlewareStep.Unauthorized);
      }
    }

    setIsLoading(false);
  }, [isSignedIn, sellers, currentSeller, middlewarePartners, currentMiddleware]);

  useEffect(() => {
    if (isSignedIn && user?.emailAddress) {
      setEmail(isGdkEmailEnabledForOverride(user.emailAddress) ? "" : user.emailAddress);
    }
  }, [isSignedIn, user, setEmail]);

  const handleOpenConfirmModal = () => {
    setIsConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  const onSubmit = async () => {
    setIsConfirmModalOpen(false);
    setCurrentPageStep(GdkMiddlewareStep.Submitting);
    try {
      if (currentSeller?.id && currentMiddleware && email) {
        const requestBody: GdkMiddlewareRequest = {
          sellerId: currentSeller.id,
          middlewareId: currentMiddleware.id,
          email,
        };
        const response = await fetch(sendGdkMiddlewareUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          setCurrentPageStep(GdkMiddlewareStep.Submitted);
        } else if (response.status === 401) {
          // Session expired — redirect to sign-in
          window.location.href = signInUrl;
        } else {
          console.error("GDK middleware request failed with status:", response.status);
          setCurrentPageStep(GdkMiddlewareStep.SubmissionError);
        }
      }
    } catch (error) {
      console.error("Error submitting GDK middleware request:", error);
      setCurrentPageStep(GdkMiddlewareStep.SubmissionError);
    }
  };

  const getStepContent = () => {
    switch (currentPageStep) {
      case GdkMiddlewareStep.NoPartnerCenterAccount:
      default:
        return <NoPartnerCenterAccountStep />;
      case GdkMiddlewareStep.Unauthorized:
        return <UnauthorizedStep />;
      case GdkMiddlewareStep.MainRequest:
        return (
          <MainRequestStep
            currentSeller={currentSeller}
            setCurrentSeller={setCurrentSeller}
            currentMiddleware={currentMiddleware}
            setCurrentMiddleware={setCurrentMiddleware}
            email={email}
            setEmail={setEmail}
          />
        );
      case GdkMiddlewareStep.Submitting:
        return <SubmittingStep />;
      case GdkMiddlewareStep.Submitted:
        return (
          <SubmittedStep onStartOver={() => setCurrentPageStep(GdkMiddlewareStep.MainRequest)} />
        );
      case GdkMiddlewareStep.SubmissionError:
        return (
          <SubmissionErrorStep onRetry={() => setCurrentPageStep(GdkMiddlewareStep.MainRequest)} />
        );
    }
  };

  const stepContent = getStepContent();
  const isReadyToSubmit = isGdkMiddlewareReadyToSubmit(currentSeller, currentMiddleware, email);

  return (
    <Container
      className={cx(commonStyles.pageContainer, styles.container)}
      style={{ minHeight: minPageHeight }}
    >
      {!isLoading && (
        <Row>
          <Column>
            <Row className={styles.emptyContentBox}>
              <Column>
                <h1 className={styles.title}>{gdkRequestTitle}</h1>
              </Column>
              {currentPageStep === GdkMiddlewareStep.MainRequest && (
                <Column className={styles.submitButtonColumn}>
                  <button
                    className={isReadyToSubmit ? styles.actionButton : styles.submitButtonDisabled}
                    disabled={!isReadyToSubmit}
                    onClick={handleOpenConfirmModal}
                  >
                    {submitRequest}
                  </button>
                </Column>
              )}
            </Row>
            <Row className={styles.contentBox}>
              <Column className={styles.innerContentBox}>{stepContent}</Column>
            </Row>
          </Column>
        </Row>
      )}
      <Modal isOpen={isConfirmModalOpen} onClose={handleCloseConfirmModal}>
        <div className={styles.modalHeader}>
          <h2 id="modal-title" className={styles.modalTitle}>
            {confirmAccessRequest}
          </h2>
        </div>
        <div id="modal-content" className={styles.modalBody}>
          {thirdPartyPrivacyNotice}
        </div>
        <div className={styles.modalActions}>
          <button className={styles.cancelButton} onClick={handleCloseConfirmModal} type="button">
            {cancel}
          </button>
          <button className={styles.actionButton} onClick={onSubmit} type="button">
            {submitRequestButton}
          </button>
        </div>
      </Modal>
    </Container>
  );
};

export default GdkMiddlewareRequestPage;
